import { getCurrentSession } from '../lib/auth.ts';
import { applyTenantRequestHeaders } from '../lib/tenantRequestContext';

export type InvestmentDailyBar = {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v?: number;
};

export type InvestmentHistoryResponse = {
  symbol: string;
  interval: '1d';
  startDate: string;
  endDate: string;
  bars: InvestmentDailyBar[];
  provider: 'tiingo' | 'twelve_data';
  fallbackUsed: boolean;
  adjustmentMode: 'raw' | 'adjusted';
  latestCompletedBarTimestamp: number;
  marketDataStatus: 'end_of_day';
  cacheStatus?: string;
  cacheLayer?: string;
  stale?: boolean;
  fetchedAt?: number;
  expectedLatestCompletedDateKey?: string;
  expectedLatestCompletedBarTimestamp?: number;
  providerAttempts?: string[];
};

export type InvestmentPositionHistoryRequest = {
  id: string;
  symbol: string;
  assetClass: string;
  entryDate: string;
  entryPrice: number;
  exitDate?: string | null;
  exitPrice?: number | null;
  quantity: number;
  isClosed: boolean;
};

function getBase() {
  const envBase = ((import.meta as any).env?.VITE_API_BASE as string)
    || ((import.meta as any).env?.VITE_SERVER_URL as string)
    || '';
  return envBase.replace(/\/+$/, '');
}


export async function getInvestmentPositionHistory(
  position: InvestmentPositionHistoryRequest,
  signal?: AbortSignal
): Promise<InvestmentHistoryResponse> {
  const id = String(position.id || '').trim();
  if (!id) throw new Error('missing_position_id');

  const session = await getCurrentSession();
  const accessToken = String(session?.access_token || '').trim();
  if (!accessToken) throw new Error('missing_access_token');

  const query = new URLSearchParams();
  if (id.startsWith('demo-')) {
    query.set('symbol', position.symbol);
    query.set('assetClass', position.assetClass);
    query.set('entryDate', position.entryDate);
    query.set('entryPrice', String(position.entryPrice));
    query.set('quantity', String(position.quantity));
    query.set('status', position.isClosed ? 'closed' : 'open');
    if (position.exitDate) query.set('exitDate', position.exitDate);
    if (Number.isFinite(Number(position.exitPrice))) query.set('exitPrice', String(position.exitPrice));
  }

  const suffix = query.size ? `?${query.toString()}` : '';
  const path = `/api/trades/${encodeURIComponent(id)}/investment-history${suffix}`;
  const base = getBase();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };
  applyTenantRequestHeaders(headers);

  const response = await fetch(base ? `${base}${path}` : path, { headers, signal });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(payload?.message || payload?.error || `HTTP ${response.status}`);
    (error as any).code = payload?.code || null;
    (error as any).status = response.status;
    throw error;
  }

  if (!payload?.history || !Array.isArray(payload.history.bars)) {
    throw new Error('invalid_investment_history_response');
  }
  return payload.history as InvestmentHistoryResponse;
}
